import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useLocation } from '@/shared/hooks/useLocation';
import { agentsApi } from '../api/agentsApi';
import { agentsKeys } from '../queryKeys';
import type { AgentMissionSnapshot } from '../types';

/** One agent's mission at this location, paired with the agent it belongs to. */
export interface AgentMissionEntry {
  roleKey: string;
  snapshot?: AgentMissionSnapshot;
}

/**
 * Read-only mission review for the authenticated practice's current location — one read per agent.
 *
 * A location holds one mission per agent, so a read that names no agent only ever returns the
 * default one's. Every agent hired here is read in parallel and cached under its own key; sharing
 * one key would let one agent's snapshot render as another's.
 *
 * An agent with no mission yet is not an error — the server answers with an empty snapshot — so a
 * just-hired agent simply contributes no rows until its first run.
 */
export function useAgentMission(roleKeys: readonly string[], enabled = true) {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();

  // `combine` keeps one memoized result for the whole set, so the page's own memos are not
  // invalidated on every render by a freshly built array.
  const { missions, isLoading, isError } = useQueries({
    queries: roleKeys.map((roleKey) => ({
      queryKey: agentsKeys.mission(locationId, roleKey),
      queryFn: () => agentsApi.getMission(locationId, roleKey),
      enabled: !!locationId && enabled,
      // Unconditional: the mission engine is the only execution path, so there is no state in
      // which this snapshot stops changing on its own.
      refetchInterval: 15_000,
      refetchIntervalInBackground: false,
      staleTime: 10_000,
    })),
    combine: (results) => ({
      missions: results.map<AgentMissionEntry>((result, index) => ({
        roleKey: roleKeys[index]!,
        snapshot: result.data,
      })),
      // Reported across the set: one agent failing must never blank the page, so the page keeps
      // showing whichever agents answered and offers a single retry for the rest.
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
    }),
  });

  // `refetchQueries`, not `invalidateQueries`: this is the practice's retry button after a read
  // failed, so it must fetch again unconditionally rather than depend on staleness.
  const refetch = useCallback(() => {
    void queryClient.refetchQueries({ queryKey: agentsKeys.missions(locationId) });
  }, [queryClient, locationId]);

  return { missions, isLoading, isError, refetch, locationId };
}
